import React, { useState, useEffect } from 'react';
import { FileText, CheckCircle, XCircle, Clock, Trash2, Loader2,
         ChevronDown, UserPlus, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const STATUS_STYLE = {
  pending:  'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  enrolled: 'bg-indigo-100 text-indigo-700',
};
const STATUS_ICON = { pending: Clock, approved: CheckCircle, rejected: XCircle, enrolled: GraduationCap };

export default function AdmissionsPage() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState('');
  const [deleting, setDeleting] = useState('');
  const [reviewNote, setReviewNote] = useState('');

  // Enroll modal state
  const [enrollModal, setEnrollModal] = useState(null);   // application to enroll
  const [classes, setClasses] = useState([]);
  const [enrollClass, setEnrollClass] = useState('');
  const [enrollAdmNum, setEnrollAdmNum] = useState('');
  const [createParent, setCreateParent] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => { loadApps(); }, [filter]);     // eslint-disable-line
  useEffect(() => {
    api.get('/academics/classes').then(r => setClasses(r.data || [])).catch(() => {});
  }, []);

  const loadApps = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admissions${filter ? `?status=${filter}` : ''}`);
      setApplications(res.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    setUpdating(id);
    try {
      await api.put(`/admissions/${id}/status`, { status, notes: reviewNote });
      toast.success(`Application ${status}`);
      setApplications(p => p.map(a => a.id === id ? { ...a, status } : a));
      setSelected(prev => prev?.id === id ? { ...prev, status } : prev);
      setReviewNote('');
    } catch (err) { toast.error(err?.detail || 'Failed'); }
    finally { setUpdating(''); }
  };

  const deleteApp = async (id) => {
    if (!window.confirm('Delete this application?')) return;
    setDeleting(id);
    try {
      await api.delete(`/admissions/${id}`);
      toast.success('Deleted');
      setApplications(p => p.filter(a => a.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (err) { toast.error(err?.detail || 'Failed'); }
    finally { setDeleting(''); }
  };

  const openEnroll = (app) => {
    setEnrollModal(app);
    setEnrollClass(classes[0]?.id || '');
    setEnrollAdmNum('');
    setCreateParent(true);
  };

  const submitEnroll = async () => {
    if (!enrollClass) { toast.error('Please select a class'); return; }
    if (!enrollAdmNum.trim()) { toast.error('Please enter an admission number'); return; }
    setEnrolling(true);
    try {
      const res = await api.post(`/admissions/${enrollModal.id}/enroll`, {
        class_id: enrollClass,
        admission_number: enrollAdmNum.trim(),
        create_parent_account: createParent,
      });
      toast.success('Student enrolled successfully!');
      if (res.data?.parent_account_created) toast.info('Parent login account created');
      setApplications(p => p.map(a => a.id === enrollModal.id ? { ...a, status: 'enrolled' } : a));
      setSelected(prev => prev?.id === enrollModal.id ? { ...prev, status: 'enrolled' } : prev);
      setEnrollModal(null);
    } catch (err) { toast.error(err?.detail || 'Enrollment failed'); }
    finally { setEnrolling(false); }
  };

  const counts = { pending: 0, approved: 0, rejected: 0, enrolled: 0 };
  applications.forEach(a => { counts[a.status] = (counts[a.status] || 0) + 1; });

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admissions-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Admissions</h1>
          <p className="text-sm text-slate-500 mt-0.5">Review and manage student admission applications</p>
        </div>
        <a href="/apply" target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors"
          data-testid="admission-portal-link">
          <UserPlus className="w-4 h-4" /> Admission Portal
        </a>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-5">
        {[
          { key: 'pending', label: 'Pending Review', color: 'text-amber-600', bg: 'bg-amber-50' },
          { key: 'approved', label: 'Approved', color: 'text-green-600', bg: 'bg-green-50' },
          { key: 'enrolled', label: 'Enrolled', color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { key: 'rejected', label: 'Rejected', color: 'text-red-600', bg: 'bg-red-50' },
        ].map(s => (
          <div key={s.key} className={`${s.bg} rounded-xl border border-slate-100 p-4 text-center cursor-pointer hover:shadow-sm transition-shadow`}
            onClick={() => setFilter(f => f === s.key ? '' : s.key)}>
            <p className={`text-2xl font-extrabold ${s.color}`}>{counts[s.key] || 0}</p>
            <p className="text-xs text-slate-500 mt-0.5 font-semibold uppercase tracking-wide">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative">
          <select value={filter} onChange={e => setFilter(e.target.value)}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white outline-none appearance-none pr-8"
            data-testid="filter-status">
            <option value="">All Applications</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="enrolled">Enrolled</option>
            <option value="rejected">Rejected</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>
        <span className="text-xs text-slate-400">{applications.length} application{applications.length !== 1 ? 's' : ''}</span>
      </div>

      <div className={`grid gap-4 ${selected ? 'grid-cols-5' : 'grid-cols-1'}`}>
        {/* Applications List */}
        <div className={selected ? 'col-span-3' : 'col-span-1'}>
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-indigo-600 animate-spin" /></div>
          ) : applications.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="font-semibold text-slate-500">No applications found</p>
              <p className="text-sm text-slate-400 mt-1">Share the Admission Portal link to receive applications</p>
            </div>
          ) : (
            <div className="space-y-2">
              {applications.map(app => {
                const StatusIcon = STATUS_ICON[app.status] || Clock;
                return (
                  <div key={app.id}
                    onClick={() => setSelected(s => s?.id === app.id ? null : app)}
                    className={`bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-sm ${selected?.id === app.id ? 'border-indigo-400 ring-1 ring-indigo-100' : 'border-slate-200'}`}
                    data-testid={`application-${app.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-indigo-700">{app.student?.first_name?.charAt(0)}</span>
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{app.student?.first_name} {app.student?.last_name}</p>
                          <p className="text-xs text-slate-400">
                            Applying for: <span className="font-medium">{app.student?.applying_for_class}</span>
                            {' · '}{new Date(app.created_at).toLocaleDateString('en-NG')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_STYLE[app.status]}`}>
                          <StatusIcon className="w-3 h-3" />{app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                        </span>
                        <button onClick={e => { e.stopPropagation(); deleteApp(app.id); }}
                          disabled={deleting === app.id}
                          className="text-slate-300 hover:text-red-500 p-1 rounded-lg hover:bg-red-50 transition-colors">
                          {deleting === app.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Application Detail Panel */}
        {selected && (
          <div className="col-span-2 bg-white rounded-xl border border-slate-200 p-5 sticky top-4 self-start max-h-[85vh] overflow-y-auto" data-testid="application-detail">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-800">Application Details</h3>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-600">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-sm">
              <InfoGroup title="Student">
                <InfoRow label="Name" value={`${selected.student?.first_name} ${selected.student?.last_name}`} />
                <InfoRow label="DOB" value={selected.student?.date_of_birth} />
                <InfoRow label="Gender" value={selected.student?.gender} />
                <InfoRow label="Applying For" value={selected.student?.applying_for_class} />
                <InfoRow label="Previous School" value={selected.student?.previous_school || '—'} />
              </InfoGroup>

              <InfoGroup title="Guardian">
                <InfoRow label="Name" value={selected.guardian?.name} />
                <InfoRow label="Relationship" value={selected.guardian?.relationship} />
                <InfoRow label="Phone" value={selected.guardian?.phone} />
                <InfoRow label="Email" value={selected.guardian?.email} />
                <InfoRow label="Address" value={selected.guardian?.address || '—'} />
              </InfoGroup>

              {selected.review_notes && (
                <InfoGroup title="Review Notes">
                  <p className="text-slate-600 text-sm italic">{selected.review_notes}</p>
                </InfoGroup>
              )}

              {selected.status === 'pending' && (
                <div className="border-t border-slate-100 pt-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Review Note (optional)</p>
                  <textarea value={reviewNote} onChange={e => setReviewNote(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400 resize-none h-16 mb-3"
                    placeholder="Add a note for this decision..." />
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => updateStatus(selected.id, 'approved')} disabled={updating === selected.id}
                      className="flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-bold py-2.5 rounded-lg disabled:opacity-50 transition-colors"
                      data-testid="approve-btn">
                      {updating === selected.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                      Approve
                    </button>
                    <button onClick={() => updateStatus(selected.id, 'rejected')} disabled={updating === selected.id}
                      className="flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-2.5 rounded-lg disabled:opacity-50 transition-colors"
                      data-testid="reject-btn">
                      {updating === selected.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                      Reject
                    </button>
                  </div>
                </div>
              )}

              {selected.status === 'approved' && (
                <div className="border-t border-slate-100 pt-4">
                  <button onClick={() => openEnroll(selected)}
                    className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold py-3 rounded-xl transition-colors"
                    data-testid="enroll-btn">
                    <GraduationCap className="w-4 h-4" /> Enroll as Student
                  </button>
                </div>
              )}

              {selected.status === 'enrolled' && (
                <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 text-center">
                  <GraduationCap className="w-6 h-6 mx-auto text-indigo-500 mb-1" />
                  <p className="text-sm font-bold text-indigo-700">Enrolled as Student</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Enroll Modal */}
      {enrollModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" data-testid="enroll-modal">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Enroll as Student</h3>
                <p className="text-xs text-slate-500">
                  {enrollModal.student?.first_name} {enrollModal.student?.last_name}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Class to Enroll Into</label>
                <div className="relative">
                  <select value={enrollClass} onChange={e => setEnrollClass(e.target.value)}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white outline-none focus:border-indigo-400 appearance-none pr-8"
                    data-testid="enroll-class-select">
                    <option value="">Select class...</option>
                    {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Admission Number</label>
                <input type="text" value={enrollAdmNum} onChange={e => setEnrollAdmNum(e.target.value)}
                  placeholder="e.g. GFA/2025/001"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-indigo-400"
                  data-testid="enroll-admission-number" />
              </div>

              {enrollModal.guardian?.email && (
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={createParent} onChange={e => setCreateParent(e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-indigo-600 rounded" />
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Create parent login account</p>
                    <p className="text-xs text-slate-400">{enrollModal.guardian.email} — parent can log in to view results & pay fees</p>
                  </div>
                </label>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 mt-6">
              <button onClick={() => setEnrollModal(null)}
                className="py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button onClick={submitEnroll} disabled={enrolling}
                className="py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                data-testid="confirm-enroll-btn">
                {enrolling ? <><Loader2 className="w-4 h-4 animate-spin" />Enrolling...</> : <><GraduationCap className="w-4 h-4" />Enroll Now</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoGroup({ title, children }) {
  return (
    <div className="border border-slate-100 rounded-lg overflow-hidden">
      <div className="bg-slate-50 px-3 py-1.5">
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{title}</p>
      </div>
      <div className="px-3 py-2 space-y-1.5">{children}</div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-slate-400 min-w-[90px] text-xs font-medium">{label}:</span>
      <span className="text-slate-700 font-medium text-xs">{value}</span>
    </div>
  );
}
